import moment from 'moment';
import { Utils } from 'app/utils';

export const RIDER_HISTORY_ON_ORDER_COLOR = '#FF0004';
export const RIDER_HISTORY_IDLE_COLOR = '#004060';

export interface RiderActivityDateRequest {
	RiderId: number;
	StartDate: string | null;
	EndDate: string | null;
	TimeZoneOffset: number;
}

export interface RiderActivityRow {
	Date: string;
	FirstLogin: string;
	LastLogout: string;
	TotalActivity: string;
	TotalInactivity: string;
	Logins: string;
	Logouts: string;
	DateSort: number;
}

export interface RiderHistoryPoint {
	Lat: number;
	Lng: number;
	IsOnOrder: boolean;
}

export interface RiderHistoryRouteSegment {
	points: RiderHistoryPoint[];
	isOnOrder: boolean;
}

export interface RiderHistoryRouteDrawResult {
	overlays: Array<google.maps.Polyline | google.maps.Marker>;
	polylineRoute: {
		routePath: null;
		routePaths: google.maps.Polyline[];
		points: RiderHistoryPoint[];
	};
}

export const buildRiderActivityDateRequest = (
	riderId: number,
	startDate?: Date | string | null,
	endDate?: Date | string | null
): RiderActivityDateRequest => ({
	RiderId: riderId,
	StartDate: toCalendarDate(startDate),
	EndDate: toCalendarDate(endDate),
	TimeZoneOffset: Utils.getTimezoneOffset()
});

export const mapRiderAvailabilityItems = (items: any[] | null | undefined): RiderActivityRow[] =>
	(items || []).map(item => mapRiderAvailabilityItem(item));

export const mapRiderAvailabilityItem = (item: any): RiderActivityRow => {
	const date = item?.Date ? moment(Utils.utcToLocalDate(item.Date)) : null;
	return {
		Date: date ? date.format('MM/DD/YYYY') : '',
		FirstLogin: item?.FirstLogin ? moment(Utils.utcToLocalDate(item.FirstLogin)).format('HH:mm:ss') : '',
		LastLogout: item?.LastLogout ? moment(Utils.utcToLocalDate(item.LastLogout)).format('HH:mm:ss') : '',
		TotalActivity: item?.TotalActivity ?? '',
		TotalInactivity: item?.TotalInactivity ?? '',
		Logins: item?.Logins ?? '',
		Logouts: item?.Logouts ?? '',
		DateSort: date ? date.valueOf() : 0
	};
};

export const toRiderHistoryPoints = (points: any[] | null | undefined): RiderHistoryPoint[] =>
	(points || [])
		.filter(point => point?.Lat != null && point?.Lng != null)
		.map(point => ({
			Lat: Number(point.Lat),
			Lng: Number(point.Lng),
			IsOnOrder: Boolean(point.IsOnOrder)
		}));

export const segmentRiderHistoryRoute = (points: RiderHistoryPoint[]): RiderHistoryRouteSegment[] => {
	if (!points.length) {
		return [];
	}

	const segments: RiderHistoryRouteSegment[] = [];
	let currentSegment: RiderHistoryRouteSegment = {
		isOnOrder: points[0].IsOnOrder,
		points: [points[0]]
	};

	for (let i = 1; i < points.length; i++) {
		const point = points[i];
		if (point.IsOnOrder === currentSegment.isOnOrder) {
			currentSegment.points.push(point);
			continue;
		}

		segments.push(currentSegment);
		currentSegment = {
			isOnOrder: point.IsOnOrder,
			points: [points[i - 1], point]
		};
	}

	segments.push(currentSegment);
	return segments;
};

export const drawRiderHistoryRouteOnMap = (
	map: google.maps.Map,
	points: RiderHistoryPoint[],
	riderName: string,
	riderImage: string
): RiderHistoryRouteDrawResult => {
	const overlays: Array<google.maps.Polyline | google.maps.Marker> = [];
	const segmentRoutes: google.maps.Polyline[] = [];

	if (points.length === 0) {
		return {
			overlays,
			polylineRoute: { routePath: null, routePaths: segmentRoutes, points }
		};
	}

	const infoWindow = new google.maps.InfoWindow({
		content: `
			<div style="margin-left: 5px">
				<div style="width: 50px; height: 50px; background-position: center center;
					background-size: cover; display: inline-block; font-weight: bold; border-radius:
					50%; border: 1px solid #004060; margin-right: 15px; background-image: url('${riderImage}')"></div>
				<div style="display: inline-block; float: right; text-align: center;">
					<div style="color: #333333;">${riderName}</div>
				</div>
			</div>
		`
	});

	if (points.length >= 2) {
		segmentRiderHistoryRoute(points).forEach(segment => {
			if (segment.points.length < 2) {
				return;
			}

			const segmentPath = new google.maps.Polyline({
				path: segment.points.map(point => ({ lat: point.Lat, lng: point.Lng })),
				geodesic: true,
				strokeColor: segment.isOnOrder ? RIDER_HISTORY_ON_ORDER_COLOR : RIDER_HISTORY_IDLE_COLOR,
				strokeOpacity: 1,
				strokeWeight: 5,
				map
			});

			segmentPath.addListener('click', (event: google.maps.MapMouseEvent) => {
				if (event.latLng) {
					infoWindow.setPosition(event.latLng);
					infoWindow.open(map);
				}
			});

			segmentRoutes.push(segmentPath);
			overlays.push(segmentPath);
		});
	}

	points.forEach((point, index) => {
		const marker = new google.maps.Marker({
			position: { lat: point.Lat, lng: point.Lng },
			map,
			icon: {
				url: '/Content/xpress-app/images/svg/green-ellipse-marker.svg',
				scaledSize: new google.maps.Size(13, 13)
			},
			title: `Point ${index + 1} for RiderId ${riderName}`
		});

		marker.addListener('click', () => {
			infoWindow.open(map, marker);
		});

		overlays.push(marker);
	});

	return {
		overlays,
		polylineRoute: {
			routePath: null,
			routePaths: segmentRoutes,
			points
		}
	};
};

const toCalendarDate = (value?: Date | string | null): string | null => {
	if (!value) {
		return null;
	}

	const parsed = moment(value);
	return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};
